import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { EAlertState } from "../../../../../shared/oversight-core/enums/alert-state";
import { IAlertOptions } from "../../../../../shared/oversight-core/interfaces/alert-options";
import FilteringSidePanel from "../../../../../shared/oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppDatePicker from "../../../../../shared/oversight-core/ui-elements/app-date-picker/app-date-picker";
import CheckBox from "../../../../../shared/oversight-core/ui-elements/check-box/check-box";
import {
  dayFromEndExclusiveIso,
  dayFromStartIso,
  defaultFromDay,
  fromDayStartIso,
  getDayRangeError,
  toDayEndExclusiveIso,
} from "../../../components/alert-filter-fields/alert-filter-dates";
import AlertFilterFields, {
  IAlertFilterValues,
} from "../../../components/alert-filter-fields/alert-filter-fields";
import type { ISiteErrorLogFilters } from "../site-error-log";

interface IProps {
  show: boolean;
  onClose: () => void;
  updateFilters: (changes: Partial<ISiteErrorLogFilters>) => void;
  filters: ISiteErrorLogFilters;
  zoneId?: string;
  alertOptions?: IAlertOptions;
}

const defaultAlertValues: IAlertFilterValues = {
  severity: [],
  alertState: EAlertState.ALL,
  alertName: [],
};

const SiteErrorLogFilterPanel = (props: IProps) => {
  const { show, onClose, updateFilters, filters, zoneId, alertOptions } = props;

  const [alertValues, setAlertValues] = useState<IAlertFilterValues>({
    ...defaultAlertValues,
  });
  const [filterByDate, setFilterByDate] = useState(false);
  // CheckBox is uncontrolled; a new key remounts it unchecked after Clear All.
  const [dateCheckBoxKey, setDateCheckBoxKey] = useState(0);
  const [startDate, setStartDate] = useState(defaultFromDay);
  const [endDate, setEndDate] = useState(new Date());

  const dateRangeError = filterByDate
    ? getDayRangeError(startDate, endDate)
    : undefined;
  const dateRangeValid = !dateRangeError;

  // The form shows what is applied each time the drawer opens, so a choice that was never applied
  // does not come back after the drawer is closed.
  useEffect(() => {
    if (!show) {
      return;
    }

    const hasDates = !!(filters.startDate && filters.endDate);
    setAlertValues({
      severity: filters.severity,
      alertState: filters.alertState,
      alertName: filters.alertName,
    });
    setFilterByDate(hasDates);
    setDateCheckBoxKey((key) => key + 1);
    setStartDate(
      hasDates ? dayFromStartIso(filters.startDate, zoneId) : defaultFromDay(),
    );
    setEndDate(
      hasDates ? dayFromEndExclusiveIso(filters.endDate, zoneId) : new Date(),
    );
  }, [show, filters, zoneId]);

  return (
    <FilteringSidePanel
      isOpen={show}
      onClose={onClose}
      isFilterDisabled={!dateRangeValid}
      onFilter={() => {
        if (!dateRangeValid) {
          return;
        }

        // Whole days in the site's zone: start of From to the start of the day after To, so one
        // day for both means that whole day.
        updateFilters({
          ...alertValues,
          startDate: filterByDate ? fromDayStartIso(startDate, zoneId) : "",
          endDate: filterByDate ? toDayEndExclusiveIso(endDate, zoneId) : "",
        });
        onClose();
      }}
      onClearAll={() => {
        // Clears the form and the applied filters back to the defaults, and closes the drawer.
        setAlertValues({ ...defaultAlertValues });
        setFilterByDate(false);
        setDateCheckBoxKey((key) => key + 1);
        setStartDate(defaultFromDay());
        setEndDate(new Date());
        updateFilters({ ...defaultAlertValues, startDate: "", endDate: "" });
        onClose();
      }}
    >
      <>
        <AlertFilterFields
          values={alertValues}
          onChange={setAlertValues}
          options={alertOptions}
        />
        <Row className="mt-4">
          <Col>
            <CheckBox
              key={dateCheckBoxKey}
              label="Filter by start date"
              checked={filterByDate}
              onChange={setFilterByDate}
              labelFontSize="font-size-14"
            />
          </Col>
        </Row>
        {filterByDate && (
          <>
            <Row className="mt-3">
              <Col>
                <AppDatePicker
                  label="From"
                  selectedDate={startDate}
                  onChange={setStartDate}
                  isInput
                  inputDateFormat="dd / MM / yyyy"
                  fontSize={12}
                />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <AppDatePicker
                  label="To"
                  selectedDate={endDate}
                  onChange={setEndDate}
                  isInput
                  inputDateFormat="dd / MM / yyyy"
                  fontSize={12}
                  error={dateRangeError}
                />
              </Col>
            </Row>
          </>
        )}
      </>
    </FilteringSidePanel>
  );
};

export default SiteErrorLogFilterPanel;
